import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["unreadCount", "messageList"];
  static values = {
    unreadCountUrl: String,
    recentMessagesUrl: String,
    refreshInterval: { type: Number, default: 30000 }
  };

  connect() {
    this.startPolling();
  }

  disconnect() {
    this.stopPolling();
  }

  startPolling() {
    this.fetchUnreadCount();
    this.fetchRecentMessages();

    this.pollingInterval = setInterval(() => {
      this.fetchUnreadCount();
      this.fetchRecentMessages();
    }, this.refreshIntervalValue);
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  async fetchUnreadCount() {
    try {
      const response = await fetch(this.unreadCountUrlValue, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.updateUnreadCount(data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }

  async fetchRecentMessages() {
    try {
      const response = await fetch(this.recentMessagesUrlValue, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.updateMessageList(data.recent_messages);
    } catch (error) {
      console.error('Error fetching recent messages:', error);
    }
  }

  updateUnreadCount(count) {
    if (this.hasUnreadCountTarget) {
      this.unreadCountTarget.textContent = count;

      if (count > 0) {
        this.unreadCountTarget.style.display = 'inline';
        this.unreadCountTarget.classList.add('badge', 'text-bg-danger');
      } else {
        this.unreadCountTarget.style.display = 'none';
      }
    }
  }

  updateMessageList(messages) {
    if (this.hasMessageListTarget) {
      if (messages.length === 0) {
        this.messageListTarget.innerHTML = '<li class="list-group-item text-muted">お知らせはありません</li>';
        return;
      }

      const messageItems = messages.map(notification => {
        const message = notification.message;
        const createdAt = this.formatDateTime(message.created_at);
        const roomPath = message.room.path || `/user/dm/rooms/${message.room.id}`;
        const senderName = message.sender.name;

        return `
          <li class="list-group-item p-0">
            <a href="${this.escapeAttr(roomPath)}"
               class="dropdown-item dm-notification-item">
              <span class="hd-msg-dot"></span>
              <span class="hd-msg-avatar ${this.avatarClass(senderName)}">${this.escapeHtml(this.avatarInitial(senderName))}</span>
              <div class="dm-notification-content">
                <div class="dm-notification-sender">${this.escapeHtml(senderName)}</div>
                <div class="dm-notification-room">${this.escapeHtml(message.room.name)}</div>
                <p class="dm-notification-subject">${this.escapeHtml(message.content)}</p>
              </div>
              <small class="dm-notification-time">${createdAt}</small>
            </a>
          </li>
        `;
      }).join('');

      this.messageListTarget.innerHTML = messageItems;
    }
  }

  // #1964 行アバター。ApplicationHelper#dm_avatar_class と同一ロジック
  // (変更時は両方を更新すること)。
  avatarClass(name) {
    if (name === 'PayBookサポート係') return 'hd-msg-avatar-support';

    let hash = 0;
    for (const char of name) {
      hash = ((hash * 31) + char.codePointAt(0)) >>> 0;
    }
    return `hd-msg-avatar-${(hash % 5) + 1}`;
  }

  avatarInitial(name) {
    return name === 'PayBookサポート係' ? 'Pb' : Array.from(name)[0];
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  escapeAttr(text) {
    return text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  formatDateTime(isoString) {
    const date = new Date(isoString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
  }

  refresh(event) {
    const button = event?.currentTarget;
    if (button) {
      // アニメーションを毎回先頭から再生するため一度クラスを外して reflow を挟む
      button.classList.remove('hd-spin');
      void button.offsetWidth;
      button.classList.add('hd-spin');
    }
    this.fetchUnreadCount();
    this.fetchRecentMessages();
  }
}